import type { InstrumentRunConsumables, InstrumentRunConsumablesPatch } from '@/types';

// URM-populated, always-disabled fields. `three_flow_cell_ids` is a single comma-separated
// field split into 3 display slots.
export const READ_ONLY_FIELDS = [
  { field: 'three_flow_cell_ids', labelKey: 'cons_flowcell_1', flowCellIndex: 0 },
  { field: 'three_flow_cell_ids', labelKey: 'cons_flowcell_2', flowCellIndex: 1 },
  { field: 'three_flow_cell_ids', labelKey: 'cons_flowcell_3', flowCellIndex: 2 },
  { field: 'sample_plate_id', labelKey: 'cons_sample_plate', flowCellIndex: undefined },
  { field: 'lobe_plate_id', labelKey: 'cons_detection_plate', flowCellIndex: undefined },
  { field: 'brm_tray_id', labelKey: 'cons_system_reagents', flowCellIndex: undefined },
  { field: 'buffer_base_id', labelKey: 'cons_run_buffer', flowCellIndex: undefined },
  { field: 'sys_clean_kit_sn', labelKey: 'cons_system_clean', flowCellIndex: undefined },
] as const satisfies readonly {
  field: keyof InstrumentRunConsumables;
  labelKey: string;
  flowCellIndex: number | undefined;
}[];

// The 7 fields a user (not the URM) may write, status-independent — see InstrumentRunConsumablesPatch.
export const USER_INPUT_FIELDS = [
  { field: 'run_buffer_additive_a_lot_id', labelKey: 'cons_input_additive_a', kind: 'lotId' },
  { field: 'run_buffer_additive_b_lot_id', labelKey: 'cons_input_additive_b', kind: 'lotId' },
  { field: 'run_buffer_additive_c_lot_id', labelKey: 'cons_input_additive_c', kind: 'lotId' },
  { field: 'library_prep_kit_a_sn', labelKey: 'cons_input_lib_prep_a', kind: 'sn' },
  { field: 'library_prep_kit_b_sn', labelKey: 'cons_input_lib_prep_b', kind: 'sn' },
  { field: 'library_prep_kit_c_sn', labelKey: 'cons_input_lib_prep_c', kind: 'sn' },
  { field: 'library_prep_kit_controls_sn', labelKey: 'cons_input_lib_prep_controls', kind: 'sn' },
] as const satisfies readonly {
  field: keyof InstrumentRunConsumablesPatch;
  labelKey: string;
  kind: 'lotId' | 'sn';
}[];

// The approved Balsamiq mock lays READ_ONLY_FIELDS out over 3 grid rows with trailing gaps
// (flowcells leave column 4 empty; the two plate fields leave columns 3-4 empty; the three
// system fields leave column 4 empty) so the editable fields start on row 4.
export const READ_ONLY_FIELD_ROWS = [
  READ_ONLY_FIELDS.slice(0, 3),
  READ_ONLY_FIELDS.slice(3, 5),
  READ_ONLY_FIELDS.slice(5, 8),
];

export type FieldName = (typeof USER_INPUT_FIELDS)[number]['field'];
export type FormValues = Record<FieldName, string>;

export function toFormValues(values: Partial<InstrumentRunConsumables> | null | undefined): FormValues {
  return USER_INPUT_FIELDS.reduce((acc, { field }) => {
    acc[field] = values?.[field] ?? '';
    return acc;
  }, {} as FormValues);
}

// PATCH treats omission as "leave unchanged" and null as "clear" — only send fields that actually
// changed, so a field touched concurrently by someone else is never re-sent and clobbered.
export function toPatchPayload(values: FormValues, initialValues: FormValues): InstrumentRunConsumablesPatch {
  const payload: InstrumentRunConsumablesPatch = {};
  for (const { field } of USER_INPUT_FIELDS) {
    const trimmed = values[field].trim();
    const initial = initialValues[field].trim();
    if (trimmed === initial) continue;
    payload[field] = trimmed.length > 0 ? trimmed : null;
  }
  return payload;
}
